export type TSlotCreationHours = {
  startHour: number
  endHour: number
}

export const DEFAULT_SLOT_CREATION_HOURS: TSlotCreationHours = {
  startHour: 0,
  endHour: 23,
}

/** Allowed snap intervals (minutes) for drag-create / resize on the week agenda */
export const SLOT_SNAP_MINUTE_OPTIONS = [5, 10, 15, 30, 60] as const
export type TSlotSnapMinutes = (typeof SLOT_SNAP_MINUTE_OPTIONS)[number]
export const DEFAULT_SLOT_SNAP_MINUTES: TSlotSnapMinutes = 30

export function resolveSlotCreationHours(
  office?: {
    options?: {
      recruitmentMeeting?: {
        slotCreationHours?: Partial<TSlotCreationHours> | null
      }
    }
  } | null,
): TSlotCreationHours {
  const configured = office?.options?.recruitmentMeeting?.slotCreationHours
  if (!configured) {
    return { ...DEFAULT_SLOT_CREATION_HOURS }
  }

  return {
    startHour: configured.startHour ?? DEFAULT_SLOT_CREATION_HOURS.startHour,
    endHour: configured.endHour ?? DEFAULT_SLOT_CREATION_HOURS.endHour,
  }
}

export function getAllowedSlotHourValues(hours: TSlotCreationHours): number[] {
  return Array.from({ length: 24 }, (_, hour) => hour).filter(
    hour => hour >= hours.startHour && hour <= hours.endHour,
  )
}

export function getAllowedSlotMinuteValues(hour: number, hours: TSlotCreationHours): number[] {
  const allMinutes = Array.from({ length: 12 }, (_, index) => index * 5)

  if (hour === hours.endHour && hour !== hours.startHour) {
    return [0]
  }

  return allMinutes
}

export function isSlotStartTimeAllowed(date: Date, hours: TSlotCreationHours): boolean {
  const hour = date.getHours()
  const minute = date.getMinutes()

  if (hour < hours.startHour || hour > hours.endHour) {
    return false
  }

  return getAllowedSlotMinuteValues(hour, hours).includes(minute)
}

export function clampSlotStartTime(date: Date, hours: TSlotCreationHours): Date {
  const result = new Date(date)

  if (isSlotStartTimeAllowed(result, hours)) {
    return result
  }

  if (result.getHours() < hours.startHour) {
    result.setHours(hours.startHour, 0, 0, 0)
    return result
  }

  if (result.getHours() > hours.endHour) {
    result.setHours(hours.endHour, 0, 0, 0)
    return result
  }

  result.setMinutes(0, 0, 0)
  return result
}

export function formatHourLabel(hour: number): string {
  return `${hour}h`
}
